import React from 'react'
import PropTypes from 'prop-types'
import { CMSContent, Box, ThemeProvider, Image, Columns, Placeholder, MediaQuery, Title } from '@deseretbook/react-ui'
import { RichText } from 'prismic-dom'
import { getInfo } from '@deseretbook/cms'
import { Conditional } from '../../core'
import { formatImageProps } from '../utils'
import { useMediaQuery, linkResolver, useTheme, useUserPermission } from '../'

/**
 * A ContentBlock component that renders content_block slices. This component supports the following
 * slice types:
 * - content_block_v1
 *
 * @returns {function} Component
 */
const ContentBlock = ({ slice, user }) => {
  const { type } = getInfo(slice)

  if (type !== 'content block') {
    return false
  }

  const theme = useTheme(slice.primary && slice.primary.theme)
  const mediaQuery = useMediaQuery(slice.primary && slice.primary.mediaQuery)
  const userPermission = useUserPermission(slice.primary && slice.primary.userPermission, user)
  const title = slice.primary && slice.primary.title
  const subtitle = slice.primary && slice.primary.subtitle

  const isLoading = theme.isLoading
    || mediaQuery.isLoading
    || userPermission.isLoading

  if (!userPermission.hasPermission) {
    return false
  }

  if (isLoading) {
    return (
      <Placeholder>
        <Columns isCentered>
          <MediaQuery.Small>
            <Columns.Column size={{ small: 4, medium: 3 }}>
              <Placeholder.Block height="100%" maxHeight={10} width="100%" maxWidth={10} />
            </Columns.Column>
          </MediaQuery.Small>
          <Columns.Column>
            <Placeholder.Text lines={4} />
          </Columns.Column>
        </Columns>
      </Placeholder>
    )
  }

  return (
    <Conditional
      condition={Object.keys(theme.theme).length}
      component={ThemeProvider}
      props={{ theme: theme.theme }}
    >
      <Conditional
        condition={Object.keys(mediaQuery.mediaQuery).length}
        component={MediaQuery}
        props={mediaQuery.mediaQuery}
      >
        <Box>
          {!!(title || subtitle) && <Title title={title} subtitle={subtitle} size={2} aria-level="2" />}
          {
            slice.items
              .map((item, idx) => {
                const image = item.image && item.image.url && (
                  <Columns.Column size={{ tiny: 8, small: 4, medium: 3 }}>
                    <Image {...formatImageProps(item.image)} />
                  </Columns.Column>
                )

                return (
                  /* Prismic doesn't provide anything to use as a key for these things. */
                  /* eslint-disable-next-line react/no-array-index-key */
                  <Box paddingBottom={0.5} key={idx}>
                    <Columns isCentered isMobile>
                      {item.imagePosition === 'left' && image}
                      <Columns.Column size={{ tinyOnly: 12 }}>
                        {!!(item.title || item.subtitle) && (
                          <Title title={item.title} subtitle={item.subtitle} aria-level="3" />
                        )}
                        <CMSContent
                          itsNotDangerousBecause="This html was generated by Prismic and Prismic doesn't allow writing html in their editor. 😎"
                          dangerouslySetInnerHTML={{
                            __html: RichText.asHtml(item.content, linkResolver),
                          }}
                        />
                      </Columns.Column>
                      {item.imagePosition === 'right' && image}
                    </Columns>
                  </Box>
                )
              })
          }
        </Box>
      </Conditional>
    </Conditional>
  )
}

ContentBlock.propTypes = {
  slice: PropTypes.shape({
    sliceType: PropTypes.string,
    primary: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({}),
}

ContentBlock.defaultProps = {
  slice: {},
  user: null,
}

export default ContentBlock
